/**
 * The report's arithmetic.
 *
 * Pure: no database, no clock, no fetch. The API serves the rows this viewer is allowed to
 * see and the SPA computes every figure from them, so a headline, a chart and an export can
 * never disagree — they are the same function of the same array.
 *
 * ⚠ **Scoping is NOT here.** Which rows a person may see is decided server-side, by
 * `contractorScope` / `matchesScope` in auth.ts, before anything reaches this file. Nothing
 * below may widen a row set; it only ever counts what it was given.
 */
import { addressOf, PRIORITIES, STATUS_CONTRACTOR, STATUS_UNIFIBER, isContractorDone, isPriority, } from "./quality-audits.js";
import { eventDayOf, periodKeyOf } from "./quality-flow.js";
import { AGE_BUCKETS, ageInDays, dateOnly } from "./time.js";
const EMPTY_BY_PRIORITY = () => Object.fromEntries(PRIORITIES.map((p) => [p, 0]));
/** Every headline figure, from whatever rows it is given. */
export function headlineFigures(rows) {
    const figures = {
        openPunches: 0,
        byPriority: EMPTY_BY_PRIORITY(),
        punchesWithoutPriority: 0,
        dispatched: 0,
        doneAwaitingValidation: 0,
        sentBack: 0,
        rowsWithoutPartner: 0,
    };
    for (const row of rows) {
        if (!row.isOpen)
            continue;
        figures.openPunches++;
        // An unknown label counts nowhere rather than being bucketed into P3 by accident —
        // a label added to the board on a Tuesday must show up as uncounted, not as wrong.
        // `isPriority` rather than an `in` test, for the reason given at `prioritySplit`.
        if (isPriority(row.priority))
            figures.byPriority[row.priority]++;
        else
            figures.punchesWithoutPriority++;
        if (row.statusUnifiber === STATUS_UNIFIBER.DISPATCHED)
            figures.dispatched++;
        if (isContractorDone(row))
            figures.doneAwaitingValidation++;
        else if (row.statusContractor === STATUS_CONTRACTOR.DONE)
            figures.sentBack++;
        if (row.buildContractor === null)
            figures.rowsWithoutPartner++;
    }
    return figures;
}
/**
 * The rows a filter selects.
 *
 * ⚠ This can only ever REMOVE rows. It runs on rows the server already scoped, and nothing
 * here may widen that set — a filter that could add a row would hand one contractor
 * another's punches.
 */
export function applyFilter(rows, filter) {
    const openOnly = filter.openOnly ?? true;
    return rows.filter((row) => {
        if (openOnly && !row.isOpen)
            return false;
        if (filter.partner !== undefined && row.buildContractor !== filter.partner)
            return false;
        if (filter.area !== undefined && row.area !== filter.area)
            return false;
        if (filter.priority !== undefined && row.priority !== filter.priority)
            return false;
        if (filter.statusUnifiber !== undefined && row.statusUnifiber !== filter.statusUnifiber)
            return false;
        if (filter.statusContractor !== undefined && row.statusContractor !== filter.statusContractor)
            return false;
        if (filter.typeVlsFo !== undefined) {
            const kept = filter.typeVlsFo === null ? row.typesVlsFo.length === 0 : row.typesVlsFo.includes(filter.typeVlsFo);
            if (!kept)
                return false;
        }
        if (filter.period !== undefined) {
            // The same day the chart counted the punch on — `eventDayOf` is their one definition.
            const day = eventDayOf(row, filter.period.event);
            if (day === null || periodKeyOf(day, filter.period.unit) !== filter.period.key)
                return false;
        }
        return true;
    });
}
/** How many filters are actually narrowing the view. Drives the "clear" control. */
export function activeFilterCount(filter) {
    let n = 0;
    for (const key of ["partner", "area", "priority", "statusUnifiber", "statusContractor", "typeVlsFo", "period"]) {
        if (filter[key] !== undefined)
            n++;
    }
    if (filter.openOnly === false)
        n++;
    return n;
}
/**
 * Change the filter — and clear whatever that change makes meaningless.
 *
 * ⚠ The ONE place these rules live; four click handlers used to carry copies of the first two.
 *   · A **partner** clears the commune and the priority: the drill-down runs partner →
 *     commune → priority, and a commune picked under another partner shows nothing.
 *   · A **commune** clears the priority, for the same reason one level down.
 *   · **`Validated`** unticks "open only". Only Validated closes an item (17 Sep 2026), so the
 *     two together select nothing — an option that empties the screen is a trap.
 *   · Ticking **"open only"** back clears a `Validated` status, for the same reason.
 *   · A **period** (a bar of the in-and-out chart) unticks "open only", and ticking it back
 *     removes the period. A bar counts every punch of its period, open or closed; kept under
 *     "open only", the chosen bar would show one number and the table another. So a period
 *     is never active with "open only" — for every event, not just "validated": which of a
 *     week's incoming punches are still open is what the first tile then says.
 *   · **Removing the period** ticks "open only" back: the period is what unticked it, and
 *     clicking a bar twice has to land where the first click started — not on a screen
 *     suddenly full of closed punches.
 *   · A period clears nothing else, and nothing else clears it.
 *
 * Pure: the filter it is given is not touched.
 */
export function choose(filter, patch) {
    const next = { ...filter, ...patch };
    if ("partner" in patch) {
        if (!("area" in patch))
            next.area = undefined;
        if (!("priority" in patch))
            next.priority = undefined;
    }
    if ("area" in patch && !("priority" in patch))
        next.priority = undefined;
    if (patch.statusUnifiber === STATUS_UNIFIBER.VALIDATED)
        next.openOnly = false;
    if (patch.openOnly === true && next.statusUnifiber === STATUS_UNIFIBER.VALIDATED)
        next.statusUnifiber = undefined;
    if (patch.period !== undefined)
        next.openOnly = false;
    else if ("period" in patch && filter.period !== undefined)
        next.openOnly = true;
    if (patch.openOnly === true)
        next.period = undefined;
    return next;
}
/**
 * The rows a dropdown or a chart counts: those every OTHER filter selects.
 *
 * ⚠ **Every option counts exactly what choosing it would show.** So a facet is counted
 * without its own filter — and without the filters that choosing it clears (`choose`).
 * Counted WITH its own filter, a list collapses to the one value already chosen, and the
 * values somebody might switch to vanish: that is what the status lists did until 25 Sep
 * 2026, and why a commune list counted every partner's rows after one partner was chosen.
 *
 * The Unifiber status is also counted without "open only", which is itself a filter on that
 * status (only `Validated` closes): with it, `Validated` could never be offered at all.
 */
export function facetBase(rows, filter, key) {
    const without = choose(filter, { [key]: undefined });
    if (key === "statusUnifiber")
        without.openOnly = false;
    return applyFilter(rows, without);
}
/**
 * The rows the in-and-out chart and the "last week" tile count: every filter but the period
 * and "open only".
 *
 * The rule above, applied to the chart: counted without its own filter, and without the one
 * that choosing a bar clears — "open only". A chart of punches validated per week could not
 * count a single one under "open only", which is why it says on screen that it ignores it.
 * The status filters stay: a bar counts exactly what clicking it shows, statuses included.
 */
export function flowBase(rows, filter) {
    return applyFilter(rows, { ...filter, period: undefined, openOnly: false });
}
/**
 * Count rows by one column, biggest first.
 *
 * ⚠ Rows with no value are counted under their own heading rather than dropped, and that
 * heading sorts LAST whatever its size. A bar chart whose bars do not add up to the total
 * above it is the failure this avoids — and "no partner" is a real state on this board,
 * not a data error to hide.
 */
export function countBy(rows, key, emptyLabel = "Not set") {
    const counts = new Map();
    for (const row of rows) {
        const value = row[key] ?? null;
        counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return toGroups(counts, emptyLabel);
}
/** Counted values → bars: biggest first, and the "no value" bar LAST whatever its size. */
function toGroups(counts, emptyLabel) {
    return [...counts.entries()]
        .map(([value, n]) => ({ value, label: value ?? emptyLabel, n }))
        .sort((a, b) => {
        if (a.value === null)
            return 1;
        if (b.value === null)
            return -1;
        return b.n - a.n || a.label.localeCompare(b.label);
    });
}
/**
 * Count rows by Type VLS/FO — one bar per label.
 *
 * ⚠ **The one chart whose bars do NOT add up to the total above them**, so it has to say so.
 * A punch typed `FACADE, OTHER` is a FACADE punch and an OTHER punch. Counting it once,
 * under a "FACADE, OTHER" bar, would scatter the same kind of defect across dozens of
 * combinations — in both of the orders the board stores them in — and counting it under its
 * first label only would make a bar disagree with the filter it sets. So every row counts
 * once in each bar it belongs to, and `multiValued` goes back to the screen so it can state
 * how far the bars overshoot. The other charts' rule survives in this form: **every bar
 * counts exactly the rows that clicking it shows.**
 *
 * The same label twice on one row counts once. Labels are matched character for character,
 * like every board label.
 */
export function countByTypeVlsFo(rows, emptyLabel = "Not set") {
    const counts = new Map();
    let multiValued = 0;
    for (const row of rows) {
        const labels = new Set(row.typesVlsFo);
        if (labels.size === 0)
            counts.set(null, (counts.get(null) ?? 0) + 1);
        if (labels.size > 1)
            multiValued++;
        for (const label of labels)
            counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return { groups: toGroups(counts, emptyLabel), rows: rows.length, multiValued };
}
export function prioritySplit(rows) {
    const split = { P1: 0, P2: 0, P3: 0, other: 0 };
    for (const row of rows) {
        // ⚠ `isPriority` rather than a key test on `split`: the split's own `other` key would
        // otherwise be reachable by a board label spelled "other", which would count a punch
        // as unprioritised. The predicate matches the board's three labels and nothing else.
        if (isPriority(row.priority))
            split[row.priority]++;
        else
            split.other++;
    }
    return split;
}
/** Every value of `key`, with the priority composition of each — what a stacked bar draws. */
export function countByWithPriority(rows, key, emptyLabel = "Not set") {
    const buckets = new Map();
    for (const row of rows) {
        const value = row[key] ?? null;
        const bucket = buckets.get(value);
        if (bucket)
            bucket.push(row);
        else
            buckets.set(value, [row]);
    }
    return countBy(rows, key, emptyLabel).map((group) => ({
        ...group,
        split: prioritySplit(buckets.get(group.value) ?? []),
    }));
}
/**
 * The values that actually occur in this snapshot, counted the way `facetBase` says.
 *
 * ⚠ Read from the rows, never a hard-coded list: a partner added to the board on Tuesday
 * has to appear in the filter on Wednesday without a deploy — and a label that disappeared
 * must stop being offered.
 */
export function facets(rows, filter, empty) {
    return {
        partner: countBy(facetBase(rows, filter, "partner"), "buildContractor", empty.partner),
        area: countBy(facetBase(rows, filter, "area"), "area", empty.area),
        priority: countBy(facetBase(rows, filter, "priority"), "priority", empty.priority),
        statusUnifiber: countBy(facetBase(rows, filter, "statusUnifiber"), "statusUnifiber", empty.statusUnifiber),
        statusContractor: countBy(facetBase(rows, filter, "statusContractor"), "statusContractor", empty.statusContractor),
        typeVlsFo: countByTypeVlsFo(facetBase(rows, filter, "typeVlsFo"), empty.typeVlsFo),
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Age, and the deadlines
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⚠ Two different clocks, and they must not be swapped.
 *
 *   · **Age** runs from the item's CREATION (the meeting, l.99: "some from 11 December are
 *     still open"). Known for every row — 1 611 of 1 611, measured 22 Sep 2026.
 *   · **A deadline** runs from the DISPATCH date (the decision of 17 Sep). Known for 672 of
 *     674 open punches.
 *
 * They are not interchangeable even where both exist: a punch created on 24 Nov 2025
 * carries a dispatch date of 21 Nov, three days EARLIER, because that date is typed by
 * hand while the Monday item is created afterwards.
 */
/**
 * Days allowed before a punch is late, by priority — defaults only.
 *
 * ⚠ Feature 13 makes these an administrator setting read from the database. They live in
 * one exported constant precisely so that change is a swap of the source, not a hunt.
 */
export const DEADLINE_DAYS = { P1: 7, P2: 15, P3: 30 };
/**
 * How old a row is, in whole days, as of the snapshot's day — or null when Monday gave no
 * creation instant (rows stored before the column existed).
 *
 * ⚠ `asOf` is the SNAPSHOT'S day, never `new Date()`. An age recomputed at read time would
 * tick over at midnight while the snapshot it describes is a morning fact, and two people
 * opening the same figures either side of midnight would read different ages.
 */
export function ageOf(row, asOf) {
    if (row.createdAt === null)
        return null;
    const created = new Date(row.createdAt);
    return Number.isNaN(created.getTime()) ? null : ageInDays(created, asOf);
}
/**
 * How many days past its deadline a punch is — negative when it still has time — or null
 * when the question does not apply.
 *
 * ⚠ Null is NOT zero and NOT "on time". Two kinds of row are null, each for a real reason:
 * a punch nobody prioritised has no deadline at all, and a punch with no dispatch date has
 * a deadline that cannot be counted from anywhere. Nine open rows were in that last state
 * on 22 Sep 2026. Folding either into "on time" would report work as within its deadline
 * that nobody has measured.
 */
export function daysOverdue(row, asOf, deadlines = DEADLINE_DAYS) {
    if (!isPriority(row.priority) || row.dispatchDate === null)
        return null;
    return ageInDays(dateOnly(row.dispatchDate), asOf) - deadlines[row.priority];
}
export function overdueSummary(rows, asOf, deadlines = DEADLINE_DAYS) {
    const summary = { late: 0, onTime: 0, notApplicable: 0, worstDays: null, totalLateDays: 0 };
    for (const row of rows) {
        const over = daysOverdue(row, asOf, deadlines);
        if (over === null)
            summary.notApplicable++;
        else if (over > 0) {
            summary.late++;
            summary.totalLateDays += over;
            if (summary.worstDays === null || over > summary.worstDays)
                summary.worstDays = over;
        }
        else
            summary.onTime++;
    }
    return summary;
}
/**
 * How the rows spread across the age buckets, in the buckets' own order.
 *
 * ⚠ Every bucket is returned, empty ones included, and rows with no creation date are NOT
 * in any of them — `unknown` carries those. A distribution whose bars silently omit a
 * bucket suggests the range has a hole in it.
 */
export function ageDistribution(rows, asOf) {
    const buckets = AGE_BUCKETS.map((b) => ({ label: b.label, tone: b.tone, n: 0 }));
    let unknown = 0;
    for (const row of rows) {
        const age = ageOf(row, asOf);
        if (age === null) {
            unknown++;
            continue;
        }
        const index = AGE_BUCKETS.findIndex((b) => age >= b.min && age <= b.max);
        // The buckets tile the whole range, so -1 cannot happen; counted as unknown rather
        // than dropped if it ever does.
        if (index === -1)
            unknown++;
        else
            buckets[index].n++;
    }
    return { buckets, unknown };
}
/**
 * ⚠ Dates go out as `YYYY-MM-DD`, NOT as `22 sept. 2026`.
 *
 * This is the one place in the product where a date is not in the readable format, and it
 * is deliberate: the file exists to be sorted and filtered in Excel, and `22 sept. 2026` is
 * text that sorts alphabetically — avril before janvier. ISO sorts correctly and Excel
 * recognises it as a date.
 */
const isoDay = (value) => (value === null ? "" : value.slice(0, 10));
export const EXPORT_COLUMNS = [
    { headerKey: "report.table.address", value: (r) => addressOf(r) ?? "" },
    { headerKey: "report.table.reference", value: (r) => r.name, asText: true },
    // ⚠ The board's own text, verbatim: `FACADE, OTHER` stays in ONE cell — the file splits on
    // ";", not on ",". Like every board label, a type is never translated.
    { headerKey: "report.table.typeVlsFo", value: (r) => r.typesVlsFo.join(", ") },
    { headerKey: "report.table.priority", value: (r) => r.priority ?? "" },
    // ⚠ Board labels — `CIF - Circet`, `Dispatched` — are NEVER translated. They are the
    // board's own strings, and a file whose labels differ from the board is a file nobody
    // can reconcile with it.
    { headerKey: "report.table.partner", value: (r) => r.buildContractor ?? "" },
    { headerKey: "report.table.commune", value: (r) => r.area ?? "" },
    { headerKey: "report.export.locality", value: (r) => r.laxonName ?? "" },
    { headerKey: "report.export.pop", value: (r) => r.popName ?? "" },
    { headerKey: "report.table.unifiber", value: (r) => r.statusUnifiber ?? "" },
    { headerKey: "report.table.contractor", value: (r) => r.statusContractor ?? "" },
    // The day the punch came in where the business is: `createdAt` is zoned, so its first ten
    // characters are the day in the snapshot's zone — the day the chart counts it on.
    { headerKey: "report.export.created", value: (r) => isoDay(r.createdAt) },
    { headerKey: "report.table.dispatched", value: (r) => isoDay(r.dispatchDate) },
    // ⚠ The days the in-and-out chart counts on (`eventDayOf`), so a file exported from a bar
    // shows every row inside that bar's period. Empty for a Done sent back with Redo: it is in
    // no "done" bar either.
    { headerKey: "report.export.doneOn", value: (r) => eventDayOf(r, "done") ?? "" },
    { headerKey: "report.export.validatedOn", value: (r) => eventDayOf(r, "validated") ?? "" },
    // The board's own date columns, typed by hand and mostly empty — kept because they are the
    // board's data, and headed as typed so nobody takes them for the two above.
    { headerKey: "report.export.doneTyped", value: (r) => isoDay(r.doneDate) },
    { headerKey: "report.export.validatedTyped", value: (r) => isoDay(r.validatedDate) },
    {
        headerKey: "report.export.open",
        value: (r, { t }) => t(r.isOpen ? "report.export.yes" : "report.export.no"),
    },
    { headerKey: "report.table.age", value: (r, { asOf }) => ageOf(r, asOf) },
    // ⚠ Empty, not 0, when the deadline does not apply — an unprioritised punch, or one with
    // no dispatch date. A 0 in this column would read as "exactly on its deadline".
    { headerKey: "report.table.overdue", value: (r, { asOf }) => daysOverdue(r, asOf) },
];
export function toCsv(rows, options) {
    const columns = options.columns ?? EXPORT_COLUMNS;
    const escape = (value, asText) => {
        let text = value === null || value === undefined ? "" : String(value);
        if (asText && text !== "")
            text = `\t${text}`;
        return /[";\n\r\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = [columns.map((c) => escape(options.t(c.headerKey))).join(";")];
    for (const row of rows) {
        lines.push(columns.map((c) => escape(c.value(row, options), c.asText)).join(";"));
    }
    // The BOM. It looks like nothing in a diff and it is the whole difference between
    // readable accents and mojibake.
    return `﻿${lines.join("\r\n")}\r\n`;
}
/**
 * `quality-audits_jacops-wavre_2026-09-22.csv`.
 *
 * The filter and the snapshot's date are in the name because these files get mailed on,
 * and an `export.csv` tells the next reader nothing about which day it describes — which
 * matters especially here, where the whole product is about agreeing on a day.
 *
 * ⚠ Only the dimension filters make it into the name — partner, commune, priority and Type
 * VLS/FO, in that order — and the period with its event (`valides-2026-w39`): "validated in
 * week 39" and "came in in week 39" are different files, and a name that did not say which
 * would be mailed as the other. Beyond those it stops being readable, so the status filters
 * are not in it: the screen remains the record of what was exported.
 */
export function exportFilename(parts, runDate) {
    const slug = parts
        .filter((p) => typeof p === "string" && p.trim() !== "")
        .map((p) => p
        .toLowerCase()
        .normalize("NFD")
        // Strip accents so the filename survives every mail client and file system.
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""))
        .filter(Boolean)
        .join("-");
    return `quality-audits_${slug || "all"}_${runDate}.csv`;
}
//# sourceMappingURL=quality-report.js.map